import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomers } from '@/hooks/useCustomers';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, Users, Filter, X, DollarSign, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useVisibility } from '@/hooks/useVisibility';
import { AdminOnly } from '@/components/AdminOnly';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase() || '?';

export default function Customers() {
  const navigate = useNavigate();
  const { timezone, startOfDay, endOfDay } = useTimezoneUtils();
  const { canSeeCompanyFinancials } = useVisibility();
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 300);
  const effectiveSearch = debouncedSearch.length >= 2 ? debouncedSearch : '';
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('all');
  const [lifetimeValueRange, setLifetimeValueRange] = useState([0, 50000]);
  const [jobCountFilter, setJobCountFilter] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [hasOutstanding, setHasOutstanding] = useState<string>('all');
  const [lastContactFilter, setLastContactFilter] = useState<string>('all');

  // Date filtering states
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<'created' | 'last_contact'>('created');

  const { data: allCustomers = [], isLoading } = useCustomers(effectiveSearch);

  const handleDateChange = (from: Date | undefined, to: Date | undefined) => {
    setDateFrom(from);
    setDateTo(to);
  };

  // Apply filters with useMemo for better performance
  const customers = useMemo(() => {
    return allCustomers.filter((customer) => {
      // Customer type filter
      if (customerTypeFilter !== 'all' && customer.customer_type !== customerTypeFilter) {
        return false;
      }

      // Lifetime value filter
      if (canSeeCompanyFinancials &&
          (customer.total_lifetime_value < lifetimeValueRange[0] ||
          customer.total_lifetime_value > lifetimeValueRange[1])) {
        return false;
      }

      // Job count filter
      if (jobCountFilter !== 'all') {
        const jobCount = customer.total_jobs_count;
        if (jobCountFilter === '0' && jobCount !== 0) return false;
        if (jobCountFilter === '1-3' && (jobCount < 1 || jobCount > 3)) return false;
        if (jobCountFilter === '4-10' && (jobCount < 4 || jobCount > 10)) return false;
        if (jobCountFilter === '10+' && jobCount < 10) return false;
      }

      // Tags filter
      if (selectedTags.length > 0) {
        const customerTags = customer.tags || [];
        const hasAllTags = selectedTags.every(tag => customerTags.includes(tag));
        if (!hasAllTags) return false;
      }

      // Outstanding balance filter (placeholder - would need jobs data)
      // This is simplified; in production you'd query jobs table
      if (hasOutstanding === 'yes') {
        // Filter logic would go here
      } else if (hasOutstanding === 'no') {
        // Filter logic would go here
      }

      // Last contact filter
      if (lastContactFilter !== 'all' && customer.last_contact_date) {
        const daysSince = Math.floor(
          (Date.now() - new Date(customer.last_contact_date).getTime()) / (1000 * 60 * 60 * 24)
        );

        if (lastContactFilter === 'week' && daysSince > 7) return false;
        if (lastContactFilter === 'month' && daysSince > 30) return false;
        if (lastContactFilter === '3months' && daysSince > 90) return false;
        if (lastContactFilter === '6months' && daysSince > 180) return false;
      }

      // Date filter
      if (dateFrom || dateTo) {
        let compareDate: Date | null = null;

        if (dateFilterType === 'created') {
          compareDate = customer.created_at ? new Date(customer.created_at) : null;
        } else if (dateFilterType === 'last_contact') {
          compareDate = customer.last_contact_date ? new Date(customer.last_contact_date) : null;
        }

        if (!compareDate) return false;

        if (dateFrom && compareDate < dateFrom) return false;
        if (dateTo && compareDate > dateTo) return false;
      }

      return true;
    });
  }, [allCustomers, canSeeCompanyFinancials, customerTypeFilter, lifetimeValueRange, jobCountFilter, selectedTags, hasOutstanding, lastContactFilter, dateFrom, dateTo, dateFilterType]);

  const totalCustomers = customers.length;
  const totalLifetimeValue = customers.reduce((sum, c) => sum + (c.total_lifetime_value || 0), 0);
  const avgLifetimeValue = totalCustomers > 0 ? totalLifetimeValue / totalCustomers : 0;

  // Get all unique tags from all customers
  const allTags = Array.from(
    new Set(allCustomers.flatMap(c => c.tags || []))
  ).sort();

  const activeFiltersCount =
    (customerTypeFilter !== 'all' ? 1 : 0) +
    (jobCountFilter !== 'all' ? 1 : 0) +
    (selectedTags.length) +
    (hasOutstanding !== 'all' ? 1 : 0) +
    (lastContactFilter !== 'all' ? 1 : 0) +
    (canSeeCompanyFinancials && (lifetimeValueRange[0] !== 0 || lifetimeValueRange[1] !== 50000) ? 1 : 0) +
    (dateFrom !== undefined || dateTo !== undefined ? 1 : 0);

  const clearFilters = () => {
    setCustomerTypeFilter('all');
    setLifetimeValueRange([0, 50000]);
    setJobCountFilter('all');
    setSelectedTags([]);
    setHasOutstanding('all');
    setLastContactFilter('all');
    setDateFrom(undefined);
    setDateTo(undefined);
    setDateFilterType('created');
  };

  const statCards = [
    { label: 'Total customers', value: String(totalCustomers), icon: Users },
    { label: 'Total lifetime value', value: formatCurrency(totalLifetimeValue), icon: DollarSign },
    { label: 'Avg customer value', value: formatCurrency(avgLifetimeValue), icon: TrendingUp },
  ];

  const gridColsClass = canSeeCompanyFinancials ? 'grid-cols-8' : 'grid-cols-5';

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">Manage your customer relationships</p>
          </div>
          <Button onClick={() => navigate('/customers/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Customer
          </Button>
        </div>

        <AdminOnly>
          <div className="grid grid-cols-3 gap-4">
            {statCards.map(({ label, value, icon: Icon }) => (
              <Card key={label}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-muted-foreground">{label}</p>
                      <p className="truncate text-[26px] font-semibold leading-tight tracking-tight tabular-nums">
                        {value}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </AdminOnly>

        {/* Filter row: search, sort-by, date range, filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 rounded-md pl-9"
            />
          </div>

          <Select value={dateFilterType} onValueChange={(value: 'created' | 'last_contact') => setDateFilterType(value)}>
            <SelectTrigger className="h-9 w-auto min-w-[168px] rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created">By Created date</SelectItem>
              <SelectItem value="last_contact">By Last contact date</SelectItem>
            </SelectContent>
          </Select>

          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateChange={handleDateChange}
            compareEnabled={compareEnabled}
            onCompareChange={setCompareEnabled}
            className="h-9 rounded-md"
            align="end"
          />

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 gap-2 rounded-md">
                <Filter className="h-4 w-4" />
                Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Filters</h4>
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-auto p-0 text-xs"
                    >
                      Clear all
                    </Button>
                  )}
                </div>

                <Separator />

                {/* Customer Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Customer Type</label>
                  <Select value={customerTypeFilter} onValueChange={setCustomerTypeFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="company">Company</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Lifetime Value Range */}
                {canSeeCompanyFinancials && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Lifetime Value: {formatCurrency(lifetimeValueRange[0])} - {formatCurrency(lifetimeValueRange[1])}
                    </label>
                    <Slider
                      min={0}
                      max={50000}
                      step={500}
                      value={lifetimeValueRange}
                      onValueChange={setLifetimeValueRange}
                      className="mt-2"
                    />
                  </div>
                )}

                {/* Job Count */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Number of Jobs</label>
                  <Select value={jobCountFilter} onValueChange={setJobCountFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="0">0 jobs</SelectItem>
                      <SelectItem value="1-3">1-3 jobs</SelectItem>
                      <SelectItem value="4-10">4-10 jobs</SelectItem>
                      <SelectItem value="10+">10+ jobs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tags Filter */}
                {allTags.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Tags</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {allTags.map((tag) => (
                        <Badge
                          key={tag}
                          variant={selectedTags.includes(tag) ? 'primary-soft' : 'outline'}
                          className="cursor-pointer capitalize"
                          onClick={() => {
                            setSelectedTags(prev =>
                              prev.includes(tag)
                                ? prev.filter(t => t !== tag)
                                : [...prev, tag]
                            );
                          }}
                        >
                          {tag.replace(/-/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Last Contact */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Last Contact</label>
                  <Select value={lastContactFilter} onValueChange={setLastContactFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All time</SelectItem>
                      <SelectItem value="week">This week</SelectItem>
                      <SelectItem value="month">This month</SelectItem>
                      <SelectItem value="3months">Last 3 months</SelectItem>
                      <SelectItem value="6months">Last 6 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Active Filters Display */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {customerTypeFilter !== 'all' && (
              <Badge variant="primary-soft" className="gap-1 capitalize">
                Type: {customerTypeFilter}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setCustomerTypeFilter('all')} />
              </Badge>
            )}
            {jobCountFilter !== 'all' && (
              <Badge variant="primary-soft" className="gap-1">
                Jobs: {jobCountFilter}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setJobCountFilter('all')} />
              </Badge>
            )}
            {selectedTags.map(tag => (
              <Badge key={tag} variant="primary-soft" className="gap-1 capitalize">
                {tag.replace(/-/g, ' ')}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} />
              </Badge>
            ))}
            {lastContactFilter !== 'all' && (
              <Badge variant="primary-soft" className="gap-1">
                Last contact: {lastContactFilter}
                <X className="h-3 w-3 cursor-pointer" onClick={() => setLastContactFilter('all')} />
              </Badge>
            )}
          </div>
        )}

        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className={`grid ${gridColsClass} gap-4 border-b px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground`}>
              <div className="col-span-2">Customer</div>
              <div>Phone</div>
              <div>Email</div>
              <div>Tags</div>
              {canSeeCompanyFinancials && (
                <>
                  <div>Jobs</div>
                  <div>Leads</div>
                  <div className="text-right">Lifetime value</div>
                </>
              )}
            </div>

            <div className="divide-y">
              {isLoading ? (
                <div className="py-12 text-center text-[13px] text-muted-foreground">
                  Loading customers...
                </div>
              ) : customers.length === 0 ? (
                <div className="py-12 text-center text-[13px] text-muted-foreground">
                  No customers found
                </div>
              ) : (
                customers.map((customer) => {
                  const isCompany = customer.customer_type === 'company';
                  return (
                    <div
                      key={customer.id}
                      className={`grid ${gridColsClass} items-center gap-4 px-4 py-3 text-[13px] hover:bg-muted/50 transition-colors cursor-pointer`}
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <div className="col-span-2 flex min-w-0 items-center gap-3">
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                            isCompany
                              ? 'bg-sidebar text-sidebar-foreground'
                              : 'bg-primary/10 text-primary'
                          }`}
                        >
                          {getInitials(customer.customer_name || '')}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium">{customer.customer_name}</p>
                          {customer.company_name && (
                            <p className="truncate text-[11px] text-muted-foreground">{customer.company_name}</p>
                          )}
                        </div>
                      </div>

                      <div className="text-muted-foreground tabular-nums">
                        {customer.primary_phone}
                      </div>

                      <div className="truncate text-muted-foreground">
                        {customer.primary_email || '-'}
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {(customer.tags || []).slice(0, 2).map((tag) => (
                          <Badge key={tag} variant="outline" className="capitalize">
                            {tag.replace(/-/g, ' ')}
                          </Badge>
                        ))}
                        {(customer.tags || []).length > 2 && (
                          <Badge variant="outline">
                            +{(customer.tags || []).length - 2}
                          </Badge>
                        )}
                      </div>

                      {canSeeCompanyFinancials && (
                        <>
                          <div className="font-medium tabular-nums">
                            {customer.total_jobs_count}
                          </div>

                          <div className="font-medium tabular-nums">
                            {customer.total_leads_count}
                          </div>

                          <div className="text-right font-semibold tabular-nums">
                            {formatCurrency(customer.total_lifetime_value)}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
